import { collection, addDoc, getDocs, deleteDoc, doc, getDoc, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db } from '../firebase';

const storage = getStorage();

// Función para obtener todas las imágenes de la galería
export const obtenerImagenesGaleria = async (folderId = null) => {
  try {
    let q;
    
    try {
      // Intento con ordenamiento (requiere índice compuesto)
      if (folderId) {
        // Si se especifica una carpeta, obtener solo las imágenes de esa carpeta
        q = query(
          collection(db, 'galeria'),
          where('folderId', '==', folderId),
          orderBy('fechaCreacion', 'desc')
        );
      } else {
        // Si no se especifica carpeta, obtener solo las imágenes sin carpeta (root)
        q = query(
          collection(db, 'galeria'),
          where('folderId', '==', null),
          orderBy('fechaCreacion', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (indexError) {
      console.warn("Fallback: Usando consulta sin ordenamiento debido a falta de índice.", indexError);
      
      // Fallback: consulta sin ordenamiento (no requiere índice compuesto)
      if (folderId) {
        q = query(
          collection(db, 'galeria'),
          where('folderId', '==', folderId)
        );
      } else {
        q = query(
          collection(db, 'galeria'),
          where('folderId', '==', null)
        );
      }
      
      const querySnapshot = await getDocs(q);
      
      // Ordenar manualmente los resultados por fechaCreacion
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ordenar de más reciente a más antiguo
      return results.sort((a, b) => {
        const fechaA = a.fechaCreacion ? new Date(a.fechaCreacion.seconds * 1000) : new Date(0);
        const fechaB = b.fechaCreacion ? new Date(b.fechaCreacion.seconds * 1000) : new Date(0);
        return fechaB - fechaA;
      });
    }
  } catch (error) {
    console.error('Error al obtener imágenes de la galería:', error);
    throw error;
  }
};

// Función para obtener todas las carpetas de la galería
export const obtenerCarpetasGaleria = async () => {
  try {
    const q = query(
      collection(db, 'galeria_carpetas'),
      orderBy('nombre', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error al obtener carpetas de la galería:', error);
    throw error;
  }
};

// Función para crear una nueva carpeta
export const crearCarpetaGaleria = async (nombre) => {
  try {
    const carpetaData = {
      nombre,
      fechaCreacion: new Date()
    };
    
    const docRef = await addDoc(collection(db, 'galeria_carpetas'), carpetaData);
    
    return {
      id: docRef.id,
      ...carpetaData
    };
  } catch (error) {
    console.error('Error al crear carpeta:', error);
    throw error;
  }
};

// Función para actualizar una carpeta
export const actualizarCarpetaGaleria = async (carpetaId, nuevoNombre) => {
  try {
    const carpetaRef = doc(db, 'galeria_carpetas', carpetaId);
    await updateDoc(carpetaRef, {
      nombre: nuevoNombre
    });
    
    return {
      success: true,
      mensaje: 'Carpeta actualizada correctamente'
    };
  } catch (error) {
    console.error('Error al actualizar carpeta:', error);
    throw error;
  }
};

// Función para eliminar una carpeta y opcionalmente sus imágenes
export const eliminarCarpetaGaleria = async (carpetaId, eliminarImagenes = false) => {
  try {
    // 1. Obtener imágenes de la carpeta
    const imagenes = await obtenerImagenesGaleria(carpetaId);
    
    if (imagenes.length > 0) {
      if (eliminarImagenes) {
        // Eliminar todas las imágenes de la carpeta
        const eliminarPromesas = imagenes.map(imagen => eliminarImagenGaleria(imagen.id));
        await Promise.all(eliminarPromesas);
      } else {
        // Mover las imágenes al root (sin carpeta)
        const moverPromesas = imagenes.map(imagen => {
          const imagenRef = doc(db, 'galeria', imagen.id);
          return updateDoc(imagenRef, { folderId: null });
        });
        await Promise.all(moverPromesas);
      }
    }
    
    // 2. Eliminar la carpeta
    const carpetaRef = doc(db, 'galeria_carpetas', carpetaId);
    await deleteDoc(carpetaRef);
    
    return {
      success: true,
      mensaje: eliminarImagenes 
        ? 'Carpeta e imágenes eliminadas correctamente' 
        : 'Carpeta eliminada y las imágenes movidas al nivel principal'
    };
  } catch (error) {
    console.error('Error al eliminar carpeta:', error);
    throw error;
  }
};

// Función para mover una imagen a una carpeta
export const moverImagenACarpeta = async (imagenId, carpetaId) => {
  try {
    const imagenRef = doc(db, 'galeria', imagenId);
    await updateDoc(imagenRef, {
      folderId: carpetaId // null significa nivel principal/root
    });
    
    return {
      success: true,
      mensaje: 'Imagen movida correctamente'
    };
  } catch (error) {
    console.error('Error al mover imagen:', error);
    throw error;
  }
};

// Función para subir una imagen a la galería (modificada para incluir carpeta)
export const subirImagenGaleria = async (file, metadata = {}, folderId = null) => {
  try {
    // 1. Subir archivo a Storage
    const fileName = `galeria/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    // Retornar una promesa que se resuelve cuando la subida termine
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          // Opcional: Callback para progreso de subida
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Progreso de subida: ${progress.toFixed(0)}%`);
        },
        (error) => {
          // Callback para error
          console.error('Error al subir imagen:', error);
          reject(error);
        },
        async () => {
          // Subida completada exitosamente
          try {
            // Obtener URL de descarga
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // 2. Guardar referencia en Firestore
            const imagenData = {
              url: downloadURL,
              nombre: file.name,
              tamaño: file.size,
              tipo: file.type,
              fechaCreacion: new Date(),
              metadata: metadata,
              folderId: folderId
            };
            
            const docRef = await addDoc(collection(db, 'galeria'), imagenData);
            
            // Retornar datos de la imagen + ID
            resolve({
              id: docRef.id,
              ...imagenData
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error en proceso de subida de imagen:', error);
    throw error;
  }
};

// Función para actualizar metadatos de una imagen
export const actualizarMetadataImagen = async (imagenId, nuevoMetadata) => {
  try {
    const imagenRef = doc(db, 'galeria', imagenId);
    
    // Extraer los campos que pertenecen a los metadatos
    const { nombre, folderId, ...metadataFields } = nuevoMetadata;
    
    // Actualizar solo el campo metadata
    await updateDoc(imagenRef, {
      metadata: metadataFields
    });
    
    // Si se proporciona un nombre, actualizar ese campo también
    if (nombre !== undefined) {
      await updateDoc(imagenRef, { nombre });
    }
    
    // Si se proporciona un folderId, actualizar ese campo también
    if (folderId !== undefined) {
      await updateDoc(imagenRef, { folderId });
    }
    
    return {
      success: true,
      mensaje: 'Información de imagen actualizada correctamente'
    };
  } catch (error) {
    console.error('Error al actualizar información de imagen:', error);
    throw error;
  }
};

// Función para eliminar una imagen de la galería
export const eliminarImagenGaleria = async (imagenId) => {
  try {
    // 1. Obtener documento para conocer la URL
    const imagenRef = doc(db, 'galeria', imagenId);
    const imagenSnap = await getDoc(imagenRef);
    
    if (!imagenSnap.exists()) {
      throw new Error('La imagen no existe en la galería');
    }
    
    const imagenData = imagenSnap.data();
    const imageUrl = imagenData.url;
    
    // 2. Eliminar archivo de Storage
    try {
      const storageRef = ref(storage, imageUrl);
      await deleteObject(storageRef);
    } catch (storageError) {
      console.warn('Error al eliminar imagen de Storage (puede que ya no exista):', storageError);
      // Continuamos con la eliminación del documento en Firestore
    }
    
    // 3. Eliminar documento de Firestore
    await deleteDoc(imagenRef);
    
    return {
      success: true,
      mensaje: 'Imagen eliminada correctamente'
    };
  } catch (error) {
    console.error('Error al eliminar imagen de la galería:', error);
    throw error;
  }
};

// Función para buscar imágenes por etiquetas o nombre
export const buscarImagenes = async (termino, folderId = null) => {
  try {
    // Primero obtenemos todas las imágenes, posiblemente filtradas por carpeta
    const imagenes = await obtenerImagenesGaleria(folderId);
    
    // Filtrar imágenes que coincidan con el término de búsqueda
    const resultados = imagenes.filter(imagen => {
      const nombre = imagen.nombre?.toLowerCase() || '';
      const etiquetas = imagen.metadata?.etiquetas || [];
      const descripcion = imagen.metadata?.descripcion?.toLowerCase() || '';
      
      const terminoLower = termino.toLowerCase();
      
      return nombre.includes(terminoLower) || 
             descripcion.includes(terminoLower) ||
             etiquetas.some(etiqueta => etiqueta.toLowerCase().includes(terminoLower));
    });
    
    return resultados;
  } catch (error) {
    console.error('Error al buscar imágenes:', error);
    throw error;
  }
}; 